/*
 * Project: Goalscape
 * File:    AddProjectModal.js
 * Path:    src/components/AddProjectModal.js
 * Description: Modal component for creating a new project.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement form validation and error handling for a better user experience. Consider using a schema validation library like Yup.
 */
import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { initialColumns, columnOrder } from '../constants/initialColumns';
import BackgroundPicker from './BackgroundPicker';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
    Form,
    FormGroup,
    Label,
    Input,
    TextArea,
    ButtonGroup,
    CancelButton,
    CreateButton,
    HelperText,
    SecondaryButton
} from '../styles/AddProjectModal.styles';

const AddProjectModal = ({ onClose }) => {
    const { user } = useAuth();
    const { workspaces, isLoading: isLoadingWorkspaces } = useWorkspaces(user);
    const [formData, setFormData] = useState({
        title: '',
        tagline: '',
        description: '',
        url: '',
        workspaceId: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
    const [selectedBackground, setSelectedBackground] = useState(null);
    useBodyScrollLock();

    const handleSelectBackground = (background) => {
        setSelectedBackground(background);
        setShowBackgroundPicker(false);
    };

    const handleOpenBackgroundPicker = () => {
        setShowBackgroundPicker(true);
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const cleanFormData = Object.entries(formData).reduce((acc, [key, value]) => {
                if (value.trim() !== '') {
                    acc[key] = value.trim();
                }
                return acc;
            }, {});

            const projectData = {
                ...cleanFormData,
                columns: initialColumns,
                columnOrder,
                todos: [],
                isStarred: false,
                background: selectedBackground || null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: user.uid,
                members: {
                    [user.uid]: {
                        role: 'admin',
                        addedAt: new Date().toISOString(),
                        addedBy: user.uid
                    }
                }
            };

            if (formData.workspaceId) {
                projectData.workspaceId = formData.workspaceId;
            }

            await addDoc(collection(db, 'projects'), projectData);
            onClose();
        } catch (error) {
            console.error('Error creating project:', error);
            setIsSubmitting(false);
        }
    };

    const isFormValid = () => {
        return formData.title.trim() && !isSubmitting;
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                {showBackgroundPicker ? (
                    <BackgroundPicker
                        currentBackground={selectedBackground}
                        projectTitle={formData.title}
                        onSelectBackground={handleSelectBackground}
                        onClose={() => setShowBackgroundPicker(false)}
                    />
                ) : (
                    <>
                        <ModalHeader>
                            <ModalTitle>Create New Project</ModalTitle>
                            <CloseButton onClick={onClose}>&times;</CloseButton>
                        </ModalHeader>

                        <Form onSubmit={handleSubmit}>
                            <FormGroup>
                                <Label htmlFor="title">Project Title *</Label>
                                <Input
                                    id="title"
                                    type="text"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Enter project title"
                                    required
                                    maxLength={100}
                                    autoFocus
                                />
                            </FormGroup>

                            <FormGroup>
                        <Label htmlFor="workspaceId">Workspace</Label>
                        <Input
                            as="select"
                            id="workspaceId"
                            value={formData.workspaceId}
                            onChange={handleChange}
                            disabled={isLoadingWorkspaces}
                        >
                            <option value="">Select a workspace (optional)</option>
                            {workspaces.map(ws => (
                                <option key={ws.id} value={ws.id}>{ws.name}</option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                                <Label htmlFor="tagline">Tagline</Label>
                                <Input
                                    id="tagline"
                                    type="text"
                                    value={formData.tagline}
                                    onChange={handleChange}
                                    placeholder="Brief one-line description"
                                    maxLength={150}
                                />
                                <HelperText>
                                    A short description that will be visible on the dashboard
                                </HelperText>
                            </FormGroup>

                            <FormGroup>
                                <Label htmlFor="url">URL</Label>
                                <Input
                                    id="url"
                                    type="url"
                                    value={formData.url}
                                    onChange={handleChange}
                                    placeholder="https://example.com"
                                    pattern="https?://.*"
                                />
                                <HelperText>
                                    Project URL (must start with http:// or https://)
                                </HelperText>
                            </FormGroup>

                            <FormGroup>
                                <Label htmlFor="description">Description</Label>
                                <TextArea
                                    id="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Detailed project description"
                                    maxLength={2000}
                                />
                                <HelperText>
                                    {formData.description.length}/2000 characters
                                </HelperText>
                            </FormGroup>

                    <FormGroup>
                        <Label>Project Background</Label>
                        <SecondaryButton type="button" onClick={handleOpenBackgroundPicker}>
                                    Set Background
                        </SecondaryButton>
                        {selectedBackground && (
                            <HelperText>
                                {selectedBackground.type === 'color'
                                    ? `Color: ${selectedBackground.color}`
                                    : 'Image background selected'}
                            </HelperText>
                        )}
                    </FormGroup>

                    <ButtonGroup>
                                <CancelButton type="button" onClick={onClose}>
                                    Cancel
                                </CancelButton>
                                <CreateButton
                                    type="submit"
                                    disabled={!isFormValid()}
                                >
                                    {isSubmitting ? 'Creating...' : 'Create Project'}
                                </CreateButton>
                            </ButtonGroup>
                        </Form>
                    </>
                )}
            </ModalContent>
        </ModalOverlay>
    );
};

export default AddProjectModal;
